import React, { useEffect, useMemo, useState } from "react";
import { Download, ChevronDown, FileText, AlertCircle, Loader2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/shared/components/ui/dropdown-menu";
import { BrandButton } from "@/shared/components/ui-kit";
import { FileTypeIcon } from "@/shared/components/icons/filetype";
import type { ArtifactContent } from "@/shared/types";
import { extension, toArtifact } from "./model";
import { downloadUrl, saveBlob } from "./files";
import type { ViewerFile } from "./model";
import { exportArtifact, exportFormats } from "./exportArtifact";
import type { ExportFormat } from "./exportArtifact";
import ArtifactPreview from "./ArtifactPreview";
import FilePreview from "./FilePreview";
import ViewerFrame, { DownloadButton } from "./ViewerFrame";

type Props = { title: string; onClose: () => void; description?: string; toolbar?: React.ReactNode } & (
  { mode: "artifact"; content?: ArtifactContent; file?: never } | { mode: "file"; file: ViewerFile; content?: never }
);

export default function ViewerDialog(props: Props) {
  const [activeSheet, setActiveSheet] = useState(0);
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const [error, setError] = useState("");
  const [local, setLocal] = useState<{ file: File; url: string }>();
  const file = props.file?.file instanceof File ? props.file.file : undefined;
  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file); setLocal({ file, url });
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const remoteUrl = props.file?.url;
  const url = (file && local?.file === file ? local.url : undefined) || (remoteUrl && /^(https?:|blob:|data:image\/)/i.test(remoteUrl) ? remoteUrl : undefined);
  const normalized = useMemo(() => {
    if (!props.content) return { artifact: undefined, message: "This artifact’s content is not available yet." };
    try { return { artifact: toArtifact(props.content), message: "" }; }
    catch (err) { return { artifact: undefined, message: err instanceof Error ? err.message : "Unable to read this artifact." }; }
  }, [props.content]);
  const download = () => {
    if (file) saveBlob(file, props.title);
    else if (url) downloadUrl(url, props.title);
  };
  const runExport = async (format: ExportFormat) => {
    if (!normalized.artifact || busy) return;
    setBusy(format); setError("");
    try { await exportArtifact(normalized.artifact, props.title, format, Math.min(activeSheet, normalized.artifact.sheets.length - 1)); }
    catch (err) { console.error(err); setError(err instanceof Error ? err.message : "Export failed. Please try again."); }
    finally { setBusy(null); }
  };
  const kind = normalized.artifact?.kind;
  const type = props.mode === "file" ? extension(props.title).toUpperCase() || "File" : kind === "sheet" ? "Spreadsheet" : kind === "presentation" ? "Presentation" : "Document";
  const formats: Record<ExportFormat, { label: string; detail: string }> = {
    docx: { label: "Word document", detail: "Editable document" },
    pdf: { label: "PDF document", detail: "Ready to share or print" },
    pptx: { label: "PowerPoint", detail: "Editable slides" },
    xlsx: { label: "Excel workbook", detail: "All sheets and formulas" },
    csv: { label: "CSV", detail: "Current sheet only" },
    html: { label: "HTML", detail: "Open in a browser" },
  };
  return <ViewerFrame
    title={props.title}
    icon={props.mode === "file" ? extension(props.title) : kind === "presentation" ? "pptx" : kind === "sheet" ? "xlsx" : "docx"}
    description={<>
      {props.description || type}<span aria-hidden="true" className="mx-1.5">·</span>{props.mode === "file" ? "Original file" : "Agent artifact"}
      {file && <span className="hidden sm:inline font-mono tabular-nums"> · {file.size < 1024 * 1024 ? `${Math.max(1, Math.round(file.size / 1024))} KB` : `${(file.size / 1024 / 1024).toFixed(1)} MB`}</span>}
    </>}
    busy={!!busy}
    onClose={props.onClose}
    toolbar={<>
      {props.toolbar}
      {error && <div role="alert" className="px-5 py-3 flex items-start gap-2 text-[13px] text-[var(--danger)] bg-[var(--danger-bg)] ring-1 ring-[var(--danger-ring)]"><AlertCircle size={16} className="shrink-0" />{error}</div>}
    </>}
    action={props.mode === "file" ? <DownloadButton disabled={!url} onClick={download} /> : <DropdownMenu>
      <DropdownMenuTrigger asChild><BrandButton disabled={!normalized.artifact || !!busy}>{busy ? <Loader2 size={14} className="animate-spin motion-reduce:animate-none" /> : <Download size={14} />}{busy ? "Exporting…" : "Export"}<ChevronDown size={12} /></BrandButton></DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-64 p-1.5">
        <DropdownMenuLabel className="px-2 py-2 text-[12px] font-normal text-[var(--text-secondary)]">Export {type.toLowerCase()}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {normalized.artifact && exportFormats(normalized.artifact.kind).map(format => <DropdownMenuItem aria-label={`Download ${format.toUpperCase()}${format === "csv" ? " · current sheet" : ""}`} className="gap-3 p-2.5 cursor-pointer" key={format} onSelect={() => { void runExport(format); }}>
          <FileTypeIcon kind={format} size={24} />
          <span className="flex-1"><span className="block text-[13px] font-medium">{formats[format].label}</span><span className="block mt-0.5 text-[12px] text-[var(--text-secondary)]">{formats[format].detail}</span></span>
          <span className="text-[11px] font-mono uppercase text-[var(--text-secondary)]">.{format}</span>
        </DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>}
  >
    {props.mode === "file" ? <FilePreview key={props.title} source={{ ...props.file, file }} url={url} /> : normalized.artifact ? <ArtifactPreview key={props.title + normalized.artifact.html} artifact={normalized.artifact} title={props.title} activeSheet={Math.min(activeSheet, Math.max(0, normalized.artifact.sheets.length - 1))} onSelectSheet={setActiveSheet} /> : <div role="status" className="h-full flex flex-col items-center justify-center gap-3 p-8 text-center"><FileText size={28} strokeWidth={1.5} className="text-[var(--text-secondary)]" /><h2 className="text-[15px] font-medium">Nothing to preview yet</h2><p className="max-w-xs text-[13px] leading-5 text-[var(--text-secondary)]">{normalized.message}</p></div>}
  </ViewerFrame>;
}
